import { useParams, useLocation, Link } from "react-router-dom";
import { useEffect } from "react";
import { getArticleBySlug, getRelatedArticles } from "@/mocks/blog";
import Navbar from "@/components/feature/Navbar";
import FooterSection from "@/pages/home/components/FooterSection";

const siteUrl = "https://cssbuy.team";

function buildArticleSchema(article: {
  title: string;
  slug: string;
  date: string;
  author: string;
  category: string;
  image: string;
  intro: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.intro,
    "image": article.image,
    "datePublished": article.date,
    "author": {
      "@type": "Organization",
      "name": article.author,
      "url": siteUrl
    },
    "publisher": {
      "@type": "Organization",
      "name": "CSSBuy.Team",
      "url": siteUrl,
      "logo": {
        "@type": "ImageObject",
        "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/d367cf6745ee47f3fc82c76d3a54c50c.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${siteUrl}/artigo/${article.slug}`
    },
    "inLanguage": "pt-BR"
  };
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();

  const resolvedSlug = slug || (location.pathname === "/guia-cssbuy-sheet-2026" ? "guia-cssbuy-sheet-2026" : undefined);
  const article = resolvedSlug ? getArticleBySlug(resolvedSlug) : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [resolvedSlug]);

  if (!article) {
    return (
      <main>
        <Navbar />
        <section className="min-h-screen flex items-center justify-center bg-background-50 px-6 pt-20">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-6">
              <i className="ri-error-warning-line text-accent-600 text-2xl w-8 h-8 flex items-center justify-center"></i>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">Artigo não encontrado</h1>
            <p className="text-sm text-foreground-500 mb-6 leading-relaxed">O artigo que você procura não existe ou foi removido.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-foreground-950 text-background-50 px-6 py-3 rounded-full text-sm font-semibold cursor-pointer hover:bg-foreground-800 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              Voltar ao Início
            </Link>
          </div>
        </section>
        <FooterSection />
      </main>
    );
  }

  const relatedArticles = getRelatedArticles(article.relatedSlugs);
  const articleSchema = buildArticleSchema(article);

  return (
    <main>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navbar />
      {/* Hero */}
      <section className="relative w-full min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={article.image}
            alt={article.title}
            title={article.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/40 via-foreground-950/20 to-foreground-950/60"></div>
        </div>

        <div className="relative z-10 w-full px-6 md:px-12 lg:px-20 py-20 md:py-32 pt-28 md:pt-36">
          <div className="flex flex-col lg:flex-row items-start lg:items-end gap-10 lg:gap-16">
            {/* Left Content */}
            <div className="w-full lg:w-3/5">
              <span className="inline-block bg-accent-100 text-accent-800 text-xs font-semibold px-3 py-1.5 rounded-full mb-6 whitespace-nowrap">
                {article.category}
              </span>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-background-50 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-background-50/70 text-xs mt-4">
                <span className="whitespace-nowrap">
                  <i className="ri-calendar-line mr-1 w-4 h-4 inline-flex items-center justify-center"></i>
                  {article.date}
                </span>
                <span className="whitespace-nowrap">
                  <i className="ri-user-line mr-1 w-4 h-4 inline-flex items-center justify-center"></i>
                  {article.author}
                </span>
                <span className="bg-background-50/20 text-background-50/90 px-2.5 py-0.5 rounded-full text-xs whitespace-nowrap">
                  {article.category}
                </span>
              </div>
            </div>

            {/* Right Content */}
            <div className="w-full lg:w-2/5">
              <p className="text-background-50/80 text-sm leading-relaxed mb-6 max-w-sm">
                {article.intro}
              </p>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 bg-background-50 text-foreground-950 px-6 py-3 rounded-full text-sm font-semibold cursor-pointer transition-all hover:bg-background-100 whitespace-nowrap"
                >
                  CSSBuy Sheet
                  <i className="ri-arrow-right-up-line"></i>
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 text-background-50 text-sm font-medium cursor-pointer transition-all hover:opacity-80 whitespace-nowrap border-b-2 border-background-50/30 pb-0.5"
                >
                  Buy It Now
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="relative w-full py-16 md:py-24 px-6 md:px-12 lg:px-20 bg-background-50">
        <div className="max-w-3xl mx-auto">
          {/* Intro */}
          <div className="mb-14">
            <p className="text-lg md:text-xl text-foreground-700 font-heading italic leading-relaxed">
              {article.intro}
            </p>
          </div>

          {/* Sections */}
          {article.sections.map((section, index) => (
            <div key={index} className="mb-12 md:mb-16">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5 leading-tight">
                <a id={`section-${index}`} href={`#section-${index}`} className="no-underline">
                  {section.heading}
                </a>
              </h2>
              <div
                className="text-sm md:text-base text-foreground-600 leading-relaxed space-y-4"
                dangerouslySetInnerHTML={{ __html: section.content }}
              />
            </div>
          ))}

          {/* Mid-article CTA */}
          <div className="my-14 md:my-18 bg-accent-50 rounded-2xl p-8 md:p-12 text-center">
            <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
              Não perca tempo buscando
            </h3>
            <p className="text-sm md:text-base text-foreground-600 mb-6 max-w-md mx-auto leading-relaxed">
              A <strong>melhor planilha CSSBuy</strong> já está atualizada aqui. Acesse agora e encontre os produtos que você procura.
            </p>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 bg-foreground-950 text-background-50 px-8 py-3.5 rounded-full text-sm font-semibold cursor-pointer transition-all hover:bg-foreground-800 whitespace-nowrap"
            >
              Buy It Now
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>

          {/* Back to Home */}
          <div className="text-center my-10">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-medium text-accent-700 hover:text-accent-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              Voltar para a página inicial
            </Link>
          </div>
        </div>
      </article>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="relative w-full py-16 md:py-24 px-6 md:px-12 lg:px-20 bg-background-100">
          <div className="mb-10 md:mb-14">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 leading-tight">
              Continue <span className="italic font-normal">Lendo</span>
            </h2>
            <p className="text-sm text-foreground-500 mt-2 leading-relaxed">
              Mais artigos sobre <strong>CSSBuy Spreadsheet</strong> para você explorar.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {relatedArticles.map((article) => (
              <Link
                key={article.slug}
                to={`/artigo/${article.slug}`}
                className="rounded-2xl overflow-hidden bg-background-50 border border-background-200/50 group cursor-pointer transition-all hover:border-accent-300 flex flex-col"
              >
                <div className="h-44 bg-secondary-100 flex items-center justify-center overflow-hidden">
                  <i className="ri-article-line text-5xl text-secondary-400/40 w-12 h-12 flex items-center justify-center"></i>
                </div>
                <div className="p-5 md:p-6 flex flex-col justify-between flex-1">
                  <div>
                    <h3 className="text-base md:text-lg font-heading font-bold text-foreground-950 mb-2 leading-snug group-hover:text-accent-700 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-xs text-foreground-500 leading-relaxed line-clamp-3">
                      {article.summary}
                    </p>
                  </div>
                  <span className="text-xs font-medium text-accent-600 mt-3 whitespace-nowrap">
                    Ler artigo →
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <FooterSection />
    </main>
  );
}